import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import type React from "react";
import {
  href,
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useNavigate,
  useRouteError,
} from "react-router";

import type { Route } from "./+types/root";

import { captureException, withScope } from "~/instrument";

import type { MessageDescriptor } from "~/intl";
import { defineMessages, FormattedMessage } from "~/intl";

import { CurrentUserProvider } from "~/providers/current-user-provider";
import { DefaultQueryClientProvider } from "~/providers/default-query-client-provider";
import { GlobalErrorsProvider } from "~/providers/global-error-provider";
import { LocaleProvider } from "~/providers/locale-provider";

import { HttpResponseSchemaMismatchError } from "~/services/http";
import { getUserToken } from "~/services/session";

import { Button } from "@nephroflow/design-system/components/button";
import { SnackbarContainer } from "@nephroflow/design-system/components/snackbar";
import ErrorDialog from "~/components/error-dialog";
import { Loading } from "~/components/loading";
import { Heading } from "~/shared/components/heading";

import rootCSS from "./root.css?url";

export const meta: Route.MetaFunction = () => [
  { charSet: "utf-8" },
  { title: "Workbench" },
  { name: "viewport", content: "width=device-width, initial-scale=1" },
];

export const links: Route.LinksFunction = () => [
  { rel: "icon", href: "/favicon.ico" },
  { rel: "preconnect", href: "https://rsms.me/" },
  { rel: "stylesheet", href: "https://rsms.me/inter/inter.css" },
  { rel: "stylesheet", href: rootCSS },
];

export async function clientLoader() {
  return getUserToken();
}

export default function App() {
  const currentUserToken = useLoaderData<typeof clientLoader>();

  return (
    <SnackbarContainer limit={5}>
      <GlobalErrorsProvider>
        <DefaultQueryClientProvider>
          {!currentUserToken ? (
            <LocaleProvider userFetchEnabled={false}>
              <Outlet />
            </LocaleProvider>
          ) : (
            <CurrentUserProvider>
              <LocaleProvider userFetchEnabled>
                <Outlet />
                <ErrorDialog />
              </LocaleProvider>
            </CurrentUserProvider>
          )}

          <ReactQueryDevtools initialIsOpen={false} />
        </DefaultQueryClientProvider>
      </GlobalErrorsProvider>
    </SnackbarContainer>
  );
}
export function ErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();

  if (error && error instanceof Error) {
    reportToSentry(error);

    if (error instanceof HttpResponseSchemaMismatchError) {
      console.log(error, {
        httpRequest: error.httpRequest,
        responseData: error.responseData,
        schemaErrors: error.schemaErrors,
      });
    } else {
      console.log(error);
    }
  }

  const statusMessageMap: Record<number, MessageDescriptor> = {
    404: t.errorNotFound,
    403: t.errorUnauthorized,
  };

  const errorMessage = (isRouteErrorResponse(error) ? statusMessageMap[error.status] : null) || t.errorFallbackMessage;

  return (
    <GlobalErrorsProvider>
      <DefaultQueryClientProvider>
        <LocaleProvider userFetchEnabled>
          <div className="mt-16 flex flex-col items-center">
            <Heading level={1} className="mb-2 text-2xl">
              <FormattedMessage {...t.errorTitle} />
            </Heading>
            <span className="mb-8 text-lg">
              <FormattedMessage {...errorMessage} />
            </span>
            <Button meaning="neutral" importance="primary" onClick={() => navigate(href("/"))}>
              <FormattedMessage {...t.returnHome} />
            </Button>
          </div>
        </LocaleProvider>
      </DefaultQueryClientProvider>
    </GlobalErrorsProvider>
  );
}

export function HydrateFallback() {
  return <Loading.Logo className="h-screen" />;
}

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body className="h-screen bg-blue-10">
        <div id="root">{children}</div>
        <div id="portal-root"></div>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

function reportToSentry(error: Error) {
  withScope((scope) => {
    if (error instanceof HttpResponseSchemaMismatchError) {
      scope.setContext("httpRequest", {
        url: error.httpRequest.url,
        method: error.httpRequest.method,
      });
      scope.setContext("schemaErrors", error.schemaErrors);
      scope.setExtra("responseData", error.responseData);
    }

    captureException(error);
  });
}

const t = defineMessages({
  errorTitle: {
    id: "error_title",
    defaultMessage: "Oops!",
  },
  errorNotFound: {
    id: "error_not_found",
    defaultMessage: "Page not found",
  },
  errorUnauthorized: {
    id: "error_unauthorized",
    defaultMessage: "You're not authorized to view this page",
  },
  errorFallbackMessage: {
    id: "error_fallback_message",
    defaultMessage: "Something went wrong",
  },
  returnHome: {
    id: "return_home",
    defaultMessage: "Return home",
  },
});
